import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { flatFeeService } from "../../../services/admin/flatFee.service";
import {
	setCreateCreativeState,
	setGeneralProgressHide,
	setGeneralProgressShow,
	setGeneralSnackbarState
} from "../../common/ui";


export type payloadSportList = {
	page: string | number | undefined;
	perPage: string | number | undefined;
	filters: {
	  affiliateIds?: [],
	  trackerIds?: [],
	  brandCodes?: [],
	  statuses?: [],
    dateFrom?: string;
    dateTo?: string;
  }
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
	totalEntries: number;
	startRowNum: number;
	totalPages: number;
	endRowNum: number;
}

export type flatFeeSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
	deleteFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	createFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	updateFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	download: {
		data: any | null,
		error: any,
		isFetching: boolean,
	},
};

const initialState: flatFeeSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
	deleteFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	createFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	updateFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	download: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'admin/flatFee/geListData',
  async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await flatFeeService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const deleteFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/deleteFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.deleteFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 200) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const createFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/createFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.createFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 201) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const updateFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/updateFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.updateFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 200) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const setDownloadData: any = createAsyncThunk(
	'admin/flatFee/setDownloadData',
	async (payload: any, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.setDownload(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const flatFeeSlice = createSlice({
  name: 'flatFee',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
	  clearDeleteState(state) {
      state.deleteFlatFee.error = null;
      state.deleteFlatFee.isFetching = false;
      state.deleteFlatFee.data = null;
    },
	  clearCreateFlatFeeState(state) {
      state.createFlatFee.error = null;
      state.createFlatFee.isFetching = false;
      state.createFlatFee.data = null;
    },
	  clearUpdateFlatFeeState(state) {
      state.updateFlatFee.error = null;
      state.updateFlatFee.isFetching = false;
      state.updateFlatFee.data = null;
    },
	  clearDownload(state) {
		  state.download.data = null;
		  state.download.error = null;
		  state.download.isFetching = false;
	  },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<flatFeeSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
	    state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

		builder.addCase(deleteFlatFeeData.pending, (state ) => {
      state.deleteFlatFee.isFetching = true;
      state.deleteFlatFee.error = null;
    });
    builder.addCase(deleteFlatFeeData.fulfilled, (state , action) => {
      state.deleteFlatFee.error = null;
      state.deleteFlatFee.isFetching = false;
	    state.deleteFlatFee.data = action.payload;
    });
    builder.addCase(deleteFlatFeeData.rejected, (state , action) => {
      state.deleteFlatFee.isFetching = false;
      state.deleteFlatFee.error = action.payload;
      state.deleteFlatFee.data = null;
    });

		builder.addCase(createFlatFeeData.pending, (state ) => {
      state.createFlatFee.isFetching = true;
      state.createFlatFee.error = null;
    });
    builder.addCase(createFlatFeeData.fulfilled, (state , action) => {
      state.createFlatFee.error = null;
      state.createFlatFee.isFetching = false;
	    state.createFlatFee.data = action.payload;
    });
    builder.addCase(createFlatFeeData.rejected, (state , action) => {
      state.createFlatFee.isFetching = false;
      state.createFlatFee.error = action.payload;
      state.createFlatFee.data = null;
    });

		builder.addCase(updateFlatFeeData.pending, (state ) => {
      state.updateFlatFee.isFetching = true;
      state.updateFlatFee.error = null;
    });
    builder.addCase(updateFlatFeeData.fulfilled, (state , action) => {
      state.updateFlatFee.error = null;
      state.updateFlatFee.isFetching = false;
	    state.updateFlatFee.data = action.payload;
    });
    builder.addCase(updateFlatFeeData.rejected, (state , action) => {
      state.updateFlatFee.isFetching = false;
      state.updateFlatFee.error = action.payload;
      state.updateFlatFee.data = null;
    });

	  builder.addCase(setDownloadData.pending, (state ) => {
		  state.download.isFetching = true;
		  state.download.error = null;
	  });
	  builder.addCase(setDownloadData.fulfilled, (state , action) => {
		  state.download.error = null;
		  state.download.isFetching = false;
		  state.download.data = action.payload;
	  });
	  builder.addCase(setDownloadData.rejected, (state , action) => {
		  state.download.isFetching = false;
		  state.download.error = action.payload;
	  });
  },
});
export const {
  clearListState,
	clearDeleteState,
	clearCreateFlatFeeState,
	clearUpdateFlatFeeState,
	clearDownload,
} = flatFeeSlice.actions;
export default flatFeeSlice.reducer;
